import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Clock, XCircle } from "@phosphor-icons/react";
import { API, fmtErr } from "../lib/api";
import { Button } from "@/components/ui/button";

export default function BillingReturn() {
  const [params] = useSearchParams();
  const subId = params.get("subscription_id") || "";
  const [state, setState] = useState("verifying");
  const [message, setMessage] = useState("");
  const attempts = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!subId) {
      setState("failed");
      setMessage("Missing subscription reference.");
      return;
    }
    const poll = async () => {
      try {
        const r = await API.get(`/billing/status/${subId}`);
        if (r.data.access_active) {
          setState("active");
          return;
        }
        if (["CANCELLED", "EXPIRED", "FAILED"].includes(r.data.status)) {
          setState("failed");
          setMessage("The payment was not completed.");
          return;
        }
        attempts.current += 1;
        if (attempts.current < 10) {
          timer.current = setTimeout(poll, 3000);
        } else {
          setState("pending");
        }
      } catch (e) {
        setState("failed");
        setMessage(fmtErr(e));
      }
    };
    poll();
    return () => clearTimeout(timer.current);
  }, [subId]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center" data-testid="billing-return-page">
      <div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-8 text-center">
        {state === "verifying" && (
          <>
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <h1 className="font-heading text-xl font-bold">Verifying your payment…</h1>
            <p className="text-sm text-muted-foreground">Do not close this page.</p>
          </>
        )}
        {state === "active" && (
          <>
            <CheckCircle size={48} weight="fill" className="mx-auto text-[#008A52]" />
            <h1 className="font-heading text-xl font-bold" data-testid="billing-success">Subscription Active</h1>
            <p className="text-sm text-muted-foreground">Your plan has been upgraded. Enjoy the new limits!</p>
          </>
        )}
        {state === "pending" && (
          <>
            <Clock size={48} className="mx-auto text-[#B7791F]" />
            <h1 className="font-heading text-xl font-bold" data-testid="billing-pending">Payment Pending</h1>
            <p className="text-sm text-muted-foreground">Your bank is still confirming the mandate. This usually takes a few minutes — your plan activates automatically.</p>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle size={48} weight="fill" className="mx-auto text-[#E02424]" />
            <h1 className="font-heading text-xl font-bold" data-testid="billing-failed">Payment Not Completed</h1>
            <p className="text-sm text-muted-foreground">{message || "Please try again from the Subscription page."}</p>
          </>
        )}
        <div className="flex justify-center gap-2 pt-2">
          <Link to="/billing"><Button variant="outline" data-testid="return-to-billing-button">Subscription</Button></Link>
          <Link to="/dashboard"><Button data-testid="return-to-dashboard-button">Dashboard</Button></Link>
        </div>
      </div>
    </div>
  );
}
